import { Checkbox, FormControlLabel } from "@mui/material";
import { useEffect, useState } from "react";
import Flex from "../../../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../../../components/Text";
import StyledFormModal from "../../../../../../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../../../../../../components/app/StyledButtonV2";
import { DisclosureHost } from "../../../../../../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../../../../../../hooks/useSnackbar";
import { useLinkTherapiesToAppointment } from "../../../../../../../../../../../../queries/encounter/use-link-therapies-to-appointment";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../../../../../../utils/utils";
import { useGetAllTherapyNotes } from "../../../../../../../../../../../../queries/encounter/use-get-all-therapy-notes";

interface Therapy {
  uuid: string;
  name: string;
  description?: string;
}

interface CommonlyUsedTherapiesModalProps {
  disclosureHost: DisclosureHost;
}

const CommonlyUsedTherapiesModal = ({
  disclosureHost,
}: CommonlyUsedTherapiesModalProps) => {
  const appointmentid = useQueryParams("appointmentid");
  const showSnackbar = useSnackbar();
  const { mutateAsync: linkTherapies, isPending } =
    useLinkTherapiesToAppointment();

  // full therapy catalog for the checkbox list, sorted by name so the visit
  // note and the encounter-summary PDF show therapies in the same order
  // (ECRH-5428).
  const { data } = useGetAllTherapyNotes();
  const therapyList: Therapy[] = [...(data?.data?.content ?? [])].sort((a, b) =>
    (a?.name ?? "").localeCompare(b?.name ?? "")
  );

  // therapies already linked to this appointment (used to prefill selections)
  const { data: linkedData, refetch: refetchLinked } = useGetAllTherapyNotes({
    appointmentId: appointmentid ?? undefined,
    enabled: !!appointmentid,
  });

  // keyed by therapy uuid
  const [therapies, setTherapies] = useState<Record<string, boolean>>({});

  // On open, prefill the checkboxes with the latest linked therapies.
  // If none are linked, nothing is prefilled.
  useEffect(() => {
    if (!disclosureHost.opened) return;
    const linked: Therapy[] = linkedData?.data?.content ?? [];
    setTherapies(
      linked.reduce<Record<string, boolean>>(
        (acc, t) => ({ ...acc, [t.uuid]: true }),
        {}
      )
    );
  }, [disclosureHost.opened, linkedData]);

  const resetAndClose = () => {
    setTherapies({});
    disclosureHost.close();
  };

  const handleSave = () => {
    const selected = Object.entries(therapies)
      .filter(([, checked]) => checked)
      .map(([uuid]) => uuid);

    linkTherapies({
      appointmentUuid: appointmentid ?? "",
      therapies: selected,
      xTENANTID: getXtenantId(),
    })
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        refetchLinked();
        resetAndClose();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={resetAndClose}
      dialogTitle="Commonly Used/Recommended Therapies"
      dialogAction={
        <>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={resetAndClose}
          />
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={handleSave}
            disabled={isPending}
            sx={{ width: "105px" }}
          />
        </>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "75vh",
        },
      }}
    >
      <Flex flexDirection="column" gap={1.5} width="100%" pt={2}>
        {therapyList.map((option) => {
          const checked = therapies[option.uuid] || false;
          return (
            <FormControlLabel
              key={option.uuid}
              onChange={(_, isChecked) =>
                setTherapies((prev) => ({
                  ...prev,
                  [option.uuid]: isChecked,
                }))
              }
              sx={{
                m: 0,
                alignItems: "flex-start",
                gap: 1,
                p: 1.5,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: checked ? "primary.main" : "divider",
                bgcolor: checked ? "action.selected" : "transparent",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
              control={
                <Checkbox
                  sx={{ p: 0, mt: "2px" }}
                  name={option.uuid}
                  checked={checked}
                />
              }
              label={
                <Flex flexDirection="column" gap={0.5}>
                  <Text fontSize="14px" fontWeight={500} color="text.primary">
                    {option.name}
                  </Text>
                  <Text fontSize="12px" color="text.secondary">
                    {option.description}
                  </Text>
                </Flex>
              }
            />
          );
        })}
      </Flex>
    </StyledFormModal>
  );
};

export default CommonlyUsedTherapiesModal;
